import { motion } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="terms-page">
      <SEOHead 
        title="Terms of Service"
        description="Terms of Service for OnPoint Authority Systems, Inc. - Terms and conditions for using our services."
        url="https://onpointauthority.com/terms"
      />
      <Navbar />

      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Legal
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Terms of<br />
              <span className="text-primary">Service</span>
            </h1>
            <p className="text-sm text-muted-foreground mb-12">
              Last updated: December 2025
            </p>

            <div className="prose prose-invert max-w-none space-y-8">
              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">1. Agreement to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing or using the website of OnPoint Authority Systems, Inc. ("Company," "we," "us," 
                  or "our"), a Delaware corporation, you agree to be bound by these Terms of Service. If you 
                  do not agree to these terms, you may not access or use our website or services.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">2. Description of Services</h2>
                <p className="text-muted-foreground leading-relaxed">
                  OnPoint Authority Systems designs institutional-grade authority, intake, and decision 
                  infrastructure for operators preparing to scale, delegate, or exit. Our services include 
                  authority system design, operator certification, system licensing, and enterprise governance 
                  frameworks.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">3. Application Process</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  All engagements begin with an Authority Review application. By submitting an application, 
                  you acknowledge and agree that:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>There is no open enrollment</li>
                  <li>Discovery calls are not offered</li>
                  <li>All applications are subject to internal authority review</li>
                  <li>Decisions (Approved, Hold, or Declined) are final</li>
                  <li>No exceptions are made to this process</li>
                </ul>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">4. Intellectual Property</h2>
                <p className="text-muted-foreground leading-relaxed">
                  All content, frameworks, methodologies, and materials on this website are the intellectual 
                  property of OnPoint Authority Systems, Inc. and are protected by copyright, trademark, and 
                  other intellectual property laws. You may not reproduce, distribute, or create derivative 
                  works without our express written permission.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">5. Confidentiality</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Information submitted through Authority Review applications is treated as confidential. 
                  We implement appropriate measures to protect your business information. However, we reserve 
                  the right to use anonymized, aggregated data for analytical purposes.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">6. No Guarantees</h2>
                <p className="text-muted-foreground leading-relaxed">
                  While we design systems intended to create institutional-grade authority infrastructure, 
                  we do not guarantee specific business outcomes, revenue results, or acquisition success. 
                  Results depend on numerous factors beyond our control.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">7. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To the maximum extent permitted by law, OnPoint Authority Systems, Inc. shall not be liable 
                  for any indirect, incidental, special, consequential, or punitive damages arising out of or 
                  related to your use of our website or services.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">8. Indemnification</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree to indemnify and hold harmless OnPoint Authority Systems, Inc., its officers, 
                  directors, employees, and agents from any claims, damages, losses, or expenses arising 
                  from your use of our website or violation of these terms.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">9. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms of Service shall be governed by and construed in accordance with the laws of 
                  the State of Delaware, without regard to its conflict of law provisions. Any disputes 
                  shall be resolved in the courts of Delaware.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">10. Changes to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We reserve the right to modify these Terms of Service at any time. Changes will be effective 
                  immediately upon posting to this website. Your continued use of our website constitutes 
                  acceptance of any modified terms.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">11. Contact</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For questions regarding these Terms of Service, contact us at:
                </p>
                <div className="mt-4 p-6 border border-white/10 bg-card/5">
                  <p className="text-foreground font-medium">OnPoint Authority Systems, Inc.</p>
                  <p className="text-muted-foreground">A Delaware Corporation</p>
                  <p className="text-primary mt-2">contact@onpointauthority.com</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
